import React from "react";
import { Button } from "@/components/ui/button";
import {
    NavigationMenu,
    NavigationMenuContent,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuTrigger,
    navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import {
    Sheet,
    SheetContent,
    SheetTrigger,
} from "@/components/ui/sheet";
import { MenuIcon, Settings } from "lucide-react";
import { Link } from "react-router";
import { useDesaDetail } from "../hooks/use-desa-public";

interface HeaderMenuProps {
    logoSrc: string;
    menuItems: {
        title: string;
        href?: string;
        description?: string;
        icon?: React.ElementType;
        children?: {
            title: string;
            href: string;
            description?: string;
            icon?: React.ElementType;
        }[];
    }[];
}

// ListItem component sesuai dengan contoh NavigationMenuDemo
function ListItem({
    title,
    children,
    href,
    icon: Icon,
    ...props
}: React.ComponentPropsWithoutRef<"li"> & {
    href: string;
    icon?: React.ElementType;
}) {
    return (
        <li {...props}>
            <NavigationMenuLink asChild>
                {href.startsWith("/") ? (
                    <Link to={href}>
                        <div className="flex items-center gap-2 text-sm leading-none font-medium">
                            {Icon && <Icon className="h-4 w-4" />}
                            {title}
                        </div>
                        {children && (
                            <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
                                {children}
                            </p>
                        )}
                    </Link>
                ) : (
                    <a href={href}>
                        <div className="flex items-center gap-2 text-sm leading-none font-medium">
                            {Icon && <Icon className="h-4 w-4" />}
                            {title}
                        </div>
                        {children && (
                            <p className="text-muted-foreground line-clamp-2 text-sm leading-snug">
                                {children}
                            </p>
                        )}
                    </a>
                )}
            </NavigationMenuLink>
        </li>
    );
}

export function HeaderMenu({ logoSrc, menuItems }: HeaderMenuProps) {
    const { data: desa } = useDesaDetail()

    return (
        <header className="w-full bg-white/95 backdrop-blur-md border-b-2 border-gray-300/80 shadow-lg sticky top-0 z-50">
            <div className="container mx-auto flex h-16 items-center px-4 sm:px-6">
                {/* {desa?.name || 'Logo Section'} */}
                <div className="mr-6 flex items-center">
                    <Link to="/" className="flex items-center group gap-3">

                        {/* Tatadesa logo/photo */}
                        <div className="relative">
                            {desa?.logoUrl ? (
                                <img src={desa.logoUrl} alt="Tatadesa" className="h-8 w-8 rounded-lg object-cover" />
                            ) : (
                                <div className="h-8 w-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                                    <span className="text-white font-bold text-sm">T</span>
                                </div>
                            )}
                        </div>
                        <span className="text-xl font-bold text-gray-800 group-hover:text-blue-600 transition-colors">
                            {desa?.name || 'Tatadesa'}
                        </span>
                    </Link>
                </div>

                {/* Desktop Navigation Menu */}
                <div className="hidden md:flex md:flex-1">
                    <NavigationMenu viewport={false}>
                        <NavigationMenuList>
                            {menuItems.map((item) =>
                                item.children ? (
                                    <NavigationMenuItem key={item.title}>
                                        <NavigationMenuTrigger>
                                            <div className="flex items-center gap-2">
                                                {item.icon && <item.icon className="h-4 w-4" />}
                                                {item.title}
                                            </div>
                                        </NavigationMenuTrigger>
                                        <NavigationMenuContent>
                                            <ul className="grid w-[400px] gap-2 p-4 md:w-[500px] md:grid-cols-2 lg:w-[600px]">
                                                {item.children.map((child) => (
                                                    <ListItem
                                                        key={child.title}
                                                        title={child.title}
                                                        href={child.href}
                                                        icon={child.icon}
                                                    >
                                                        {child.description}
                                                    </ListItem>
                                                ))}
                                            </ul>
                                        </NavigationMenuContent>
                                    </NavigationMenuItem>
                                ) : (
                                    <NavigationMenuItem key={item.title}>
                                        <NavigationMenuLink asChild className={navigationMenuTriggerStyle()}>
                                            {item.href?.startsWith("/") ? (
                                                <Link to={item.href}>
                                                    <div className="flex items-center gap-2">
                                                        {item.icon && <item.icon className="h-4 w-4" />}
                                                        {item.title}
                                                    </div>
                                                </Link>
                                            ) : (
                                                <a href={item.href}>
                                                    <div className="flex items-center gap-2">
                                                        {item.icon && <item.icon className="h-4 w-4" />}
                                                        {item.title}
                                                    </div>
                                                </a>
                                            )}
                                        </NavigationMenuLink>
                                    </NavigationMenuItem>
                                )
                            )}
                        </NavigationMenuList>
                    </NavigationMenu>
                </div>

                {/* Mobile Menu */}
                <div className="ml-auto flex items-center gap-3">
                    {/* Admin Button - Desktop */}
                    <a
                        href="/admin"
                        className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md hover:bg-gray-100 transition-colors"
                    >
                        <Settings className="h-4 w-4" />
                        Admin
                    </a>

                    {/* Mobile Admin Button */}
                    <Button asChild variant="ghost" size="icon" className="sm:hidden">
                        <a href="/admin">
                            <Settings className="h-5 w-5" />
                            <span className="sr-only">Admin Panel</span>
                        </a>
                    </Button>

                    <Sheet>
                        <SheetTrigger asChild>
                            <Button variant="ghost" size="icon" className="md:hidden">
                                <MenuIcon className="h-5 w-5" />
                                <span className="sr-only">Toggle menu</span>
                            </Button>
                        </SheetTrigger>
                        <SheetContent side="right" className="w-[320px] sm:w-[380px] bg-white">
                            <div className="flex flex-col h-full">
                                {/* Mobile Menu Header */}
                                <div className="flex items-center justify-between py-4 border-b border-gray-100">
                                    <div className="flex items-center gap-3">
                                        <img src={logoSrc} alt="Tatadesa" className="h-8 w-auto" />
                                        <span className="font-semibold text-gray-900">Menu</span>
                                    </div>
                                </div>

                                {/* Admin Section - Prominent at top */}
                                <div className="py-4 border-b border-gray-100">
                                    <a
                                        href="/admin"
                                        className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 transition-all duration-200 group"
                                    >
                                        <div className="flex items-center justify-center w-10 h-10 bg-blue-100 rounded-lg group-hover:bg-blue-200 transition-colors">
                                            <Settings className="h-5 w-5 text-blue-600" />
                                        </div>
                                        <div>
                                            <div className="font-medium text-gray-900">Admin Panel</div>
                                            <div className="text-sm text-gray-500">Kelola sistem</div>
                                        </div>
                                    </a>
                                </div>

                                {/* Navigation Menu */}
                                <nav className="flex-1 py-4 overflow-y-auto">
                                    <div className="space-y-2">
                                        {menuItems.map((item) => (
                                            <div key={item.title}>
                                                {item.href ? (
                                                    item.href.startsWith("/") ? (
                                                        <Link
                                                            to={item.href}
                                                            className="flex items-center gap-3 px-3 py-3 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-200 group"
                                                        >
                                                            <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors">
                                                                {item.icon && <item.icon className="h-4 w-4" />}
                                                            </div>
                                                            <span className="font-medium">{item.title}</span>
                                                        </Link>
                                                    ) : (
                                                        <a
                                                            href={item.href}
                                                            className="flex items-center gap-3 px-3 py-3 text-gray-700 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-all duration-200 group"
                                                        >
                                                            <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-lg group-hover:bg-gray-200 transition-colors">
                                                                {item.icon && <item.icon className="h-4 w-4" />}
                                                            </div>
                                                            <span className="font-medium">{item.title}</span>
                                                        </a>
                                                    )
                                                ) : (
                                                    <div className="px-3 py-2">
                                                        <div className="flex items-center gap-3 text-gray-600">
                                                            <div className="flex items-center justify-center w-8 h-8 bg-gray-100 rounded-lg">
                                                                {item.icon && <item.icon className="h-4 w-4" />}
                                                            </div>
                                                            <span className="font-medium">{item.title}</span>
                                                        </div>
                                                        {item.children && (
                                                            <div className="ml-11 mt-2 space-y-1">
                                                                {item.children.map((child) =>
                                                                    child.href.startsWith("/") ? (
                                                                        <Link
                                                                            key={child.title}
                                                                            to={child.href}
                                                                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                                                                        >
                                                                            {child.icon && <child.icon className="h-3 w-3" />}
                                                                            {child.title}
                                                                        </Link>
                                                                    ) : (
                                                                        <a
                                                                            key={child.title}
                                                                            href={child.href}
                                                                            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                                                                        >
                                                                            {child.icon && <child.icon className="h-3 w-3" />}
                                                                            {child.title}
                                                                        </a>
                                                                    )
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </nav>

                                {/* Footer */}
                                <div className="py-4 border-t border-gray-100">
                                    <div className="text-xs text-gray-500 text-center">
                                        © 2025 Tatadesa
                                    </div>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
}
